/**
 * <WidgetPalette> — 위젯 갤러리 (iOS 위젯 추가 패널 스타일 · 카테고리 그룹)
 *
 * 편집 모드에서 좌측 사이드바가 이 컴포넌트로 변신 (대시보드 페이지 한정).
 * 카드를 클릭하면 대시보드 하단에 해당 위젯이 추가됨.
 */
"use client";

import { getAllWidgets } from "./registry";
import type { WidgetMeta, WidgetType } from "./types";

const CATEGORY_LABEL: Record<WidgetMeta["category"], string> = {
  personal:  "개인화",
  discovery: "오늘의 발견",
  market:    "시장",
  ai:        "AI 인사이트",
  info:      "정보",
  premium:   "유료",
};

const CATEGORY_ORDER: WidgetMeta["category"][] = [
  "personal", "discovery", "market", "ai", "info", "premium",
];

/** 일부 위젯은 옵션 입력이 필요 (심볼·스코프 등) — 사전 prompt */
function promptOptionsForWidget(type: WidgetType): Record<string, unknown> | null | "cancelled" {
  if (type === "company-info") {
    const input = window.prompt(
      "기업 심볼을 입력하세요\n\n예: AAPL · NVDA · TSLA · MSFT · 005930",
      "AAPL"
    );
    if (input == null) return "cancelled";
    const sym = input.trim().toUpperCase();
    if (!sym) return "cancelled";
    return { symbol: sym };
  }
  if (type === "market-news") {
    const input = window.prompt(
      "뉴스 범위\n\n- 빈 값: 시장 전체 뉴스\n- 심볼 입력(예: AAPL): 해당 종목 뉴스",
      ""
    );
    if (input == null) return "cancelled";
    const sym = input.trim().toUpperCase();
    if (!sym) return { scope: "market" };
    return { scope: "ticker", symbol: sym };
  }
  return null;
}

interface Props {
  onAdd: (type: WidgetType, options?: Record<string, unknown>) => void;
}

export function WidgetPalette({ onAdd }: Props) {
  const handlePick = (type: WidgetType) => {
    const opts = promptOptionsForWidget(type);
    if (opts === "cancelled") return;
    onAdd(type, opts ?? undefined);
  };

  const all = getAllWidgets();
  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: all.filter((w) => w.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="dash-palette-inline">
      <div className="dash-palette-head">
        <h2>위젯 갤러리</h2>

      </div>
      <div className="dash-palette-body">
        {grouped.length === 0 ? (
          <p className="dash-empty-txt">아직 등록된 위젯이 없습니다.</p>
        ) : (
          grouped.map(({ category, items }) => (
            <div key={category} className="dash-palette-group">
              <h3 className="dash-palette-group-title">{CATEGORY_LABEL[category]}</h3>
              <div className="dash-palette-grid">
                {items.map((meta) => (
                  <button
                    key={meta.type}
                    type="button"
                    className="dash-palette-item"
                    onClick={() => handlePick(meta.type)}
                  >
                    <span className="dash-palette-icon">{meta.icon}</span>
                    <span className="dash-palette-name">{meta.name}</span>
                    <span className="dash-palette-desc">{meta.description}</span>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
